import classNames from 'classnames'
import {gsap} from 'gsap'
import {ScrollTrigger} from 'gsap/ScrollTrigger'
import * as React from 'react'
import {render} from 'storyblok-rich-text-react-renderer'

import {makeEditable, tailwindConfig, unwrapAllNodeResolvers} from '@/common/utils'
import {Grid} from '@/components/Grid'
import Heading from '@/components/Heading'
import Image from '@/components/Image'

const KeyPoints = ({backgroundColors, headingTag, heading, items, _editable}) => {
  const HeadingTag = headingTag || 'p'
  const sectionRef = React.useRef()
  const imageSlider = React.useRef()
  const contentSLider = React.useRef()
  const endTrigger = React.useRef()
  const [stepActive, setStepActive] = React.useState('')
  const mm = gsap.matchMedia()

  let keyPointRef = React.useRef([])

  const imageClassNames = [
    [
      ['md:top-[10vh] md:left-0 lg:top-[13vh] lg:left-0 lg:block rounded-2xl block'],
      ['md:top-[70vh] md:right-0  lg:top-[60vh] lg:right-0'],
    ],
    [
      ['md:top-[10vh] md:left-0 lg:top-[13vh] lg:left-0 lg:block rounded-2xl block'],
      ['md:top-[70vh] md:right-0  lg:top-[60vh] lg:right-0'],
    ],
    [
      ['md:top-[10vh] md:left-0 lg:top-[13vh] lg:left-0 lg:block'],
      ['md:top-[70vh] md:right-0  lg:top-[60vh] lg:right-0'],
    ],
  ]

  React.useEffect(() => {
    mm.add(`(min-width: ${tailwindConfig.theme.screens['lg']})`, () => {
      keyPointRef.current.forEach((item, index) => {
        ScrollTrigger.create({
          trigger: item,
          start: 'top 60%',
          end: 'bottom 60%',
          onEnter: () => {
            const indexCss = index + 1
            gsap.to(document.querySelector('.imageList li:nth-child(' + indexCss + ')'), {
              opacity: 1,
              duration: 0.2,
              ease: 'expo.easeInOut',
            })
            gsap.to(document.querySelector('.imageList li:nth-child(' + index + ')'), {
              opacity: 0,
              duration: 0.2,
              ease: 'expo.easeInOut',
            })
          },
          onLeave: () => {
            const indexCss = index
            gsap.to(document.querySelector('.imageList li:nth-child(' + indexCss + ')'), {
              opacity: 0,
              duration: 0.2,
              ease: 'expo.easeInOut',
            })
          },

          onEnterBack: () => {
            const indexCss = index + 1
            gsap.to(document.querySelector('.imageList li:nth-child(' + indexCss + ')'), {
              opacity: 1,
              duration: 0.2,
              ease: 'expo.easeInOut',
            })
          },
          onLeaveBack: () => {
            const indexCss = index + 1
            gsap.to(document.querySelector('.imageList li:nth-child(' + indexCss + ')'), {
              opacity: 0,
              duration: 0.2,
              ease: 'expo.easeInOut',
            })
          },
        })
      })
      ScrollTrigger.create({
        trigger: imageSlider.current,
        pin: true,
        pinType: 'transform',
        start: 'top+=20px top',
        end: 'top+=80px bottom',
        endTrigger: endTrigger.current,
      })
    })
    return () => mm.revert()
  }, [mm])

  React.useEffect(() => {
    mm.add(`(min-width: ${tailwindConfig.theme.screens['lg']})`, () => {
      keyPointRef.current.forEach((item, index) => {
        ScrollTrigger.create({
          id: `section-${index + 1}`,
          trigger: item,
          start: 'top 60%',
          end: 'top+=60 50%',
          onEnter: () => {
            setStepActive(index)
          },
          onEnterBack: () => {
            setStepActive(index)
          },
          onLeaveBack: () => {
            setStepActive(index - 1)
          },
        })
      })
    })
    return () => mm.revert()
  }, [mm])

  return (
    <section
      {...makeEditable(_editable)}
      className={classNames('relative lg:py-28', backgroundColors.current)}
      ref={sectionRef}
    >
      <Grid className='relative'>
        <div className='col-span-full'>
          <HeadingTag className='mb-32 space-y-6 text-center md:mb-34 lg:mb-55'>
            <Heading heading={heading} />
          </HeadingTag>
          <ul
            className='imageList absolute top-0 left-0 hidden h-full w-full md:block lg:h-screen'
            ref={imageSlider}
          >
            {items.map((item, itemIndex) => {
              return (
                <li key={item._uid} className={classNames('opacity-0 transition-all duration-300')}>
                  {item.images.map((image, imageIndex) => {
                    return (
                      <div
                        key={image.id}
                        className={classNames(
                          'absolute w-full max-w-[theme(spacing.52)] md:first-of-type:drop-shadow-[-144px_66px_68px_rgba(227,243,32,0.63)] md:last-of-type:drop-shadow-[144px_66px_68px_rgba(227,243,32,0.63)] lg:h-screen lg:min-h-[360px] lg:max-w-[theme(spacing.60)]',
                          imageClassNames[itemIndex][imageIndex]
                        )}
                      >
                        <div className='aspect-w-2 aspect-h-3'>
                          <Image
                            {...image}
                            sizes={{md: 20}}
                            fitIn={true}
                            ratio='2:3'
                            className='rounded-3xl'
                          />
                        </div>
                      </div>
                    )
                  })}
                </li>
              )
            })}
          </ul>
          <ul className='relative z-10' ref={contentSLider}>
            {items.map((item, itemIndex) => {
              return (
                <li
                  key={item._uid}
                  className={classNames(
                    'flex flex-col items-center pb-28 text-center last:mb-0 lg:min-h-[300px]'
                  )}
                  ref={(element) => (keyPointRef.current[itemIndex] = element)}
                >
                  <div
                    className={classNames(
                      'flex flex-col items-center transition-all duration-200',
                      {
                        'lg:scale-75 lg:opacity-50': stepActive !== itemIndex,
                        'scale-100 opacity-100': stepActive === itemIndex,
                      }
                    )}
                  >
                    {item.icon.filename && (
                      <div className='mb-8 flex h-[theme(spacing.26)] w-[theme(spacing.26)] items-center justify-center rounded-3xl bg-violet-400'>
                        <div className='max-h-[theme(spacing.15)] w-full max-w-[theme(spacing.15)] justify-center'>
                          <div className='aspect-w-1 aspect-h-1 w-full'>
                            <Image {...item.icon} sizes={{md: 20}} fitIn={true} ratio='1:1' />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className='heading-m-millik text-center text-purple-900'>
                      {render(item.heading, {nodeResolvers: unwrapAllNodeResolvers})}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      </Grid>
      <div ref={endTrigger}></div>
    </section>
  )
}

export default KeyPoints
